import { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import Select from 'react-select'
import {
  Loader2, User, Mail, Lock, Eye, EyeOff, UserPlus, CheckCircle2,
  AlertCircle, ShieldCheck, FileText, Phone, Globe
} from 'lucide-react'
import { register, authGoogleRegister } from '@/api/auth'
import { getCountries } from '@/api/kuizy'
import SocialButtons from '@/components/SocialButtons'
import TermsCheckbox from '@/widgets/content/TermsWidget'
import PdpaCheckbox from '@/widgets/content/PdpaWidget'
import { useGoogleLogin } from '@react-oauth/google'

export default function RegisterPage() {
  const nav = useNavigate()
  const [q] = useSearchParams()
  const emailParam = q.get('email') || ''
  const [form, setForm] = useState({
    username: '', email: '',
    first_name: '', last_name: '',
    phone_code: '+66', phone: '', country: 'TH',
    pass: '', pass2: '',
    acceptTerms: false, acceptPdpa: false
  })
  const [countries, setCountries] = useState([])
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  const [show1, setShow1] = useState(false)
  const [show2, setShow2] = useState(false)
  const [saving, setSaving] = useState(false)

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setErr(''); setOk('')
      try {
        setSaving(true)
        const payload = {
          accepted_at: new Date().toISOString()
        }

        const r = await authGoogleRegister(tokenResponse.access_token, payload)
        const data = r?.data || {}

        if (data.success || data.user) {
          localStorage.setItem('viewMode', 'admin')
          nav('/organization', { replace: true })
        } else {
          setErr('การสมัครสมาชิกด้วย Google ไม่สำเร็จ')
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data?.message || 'ไม่สามารถสมัครสมาชิกด้วย Google ได้ (อีเมลนี้อาจถูกใช้งานแล้ว)'
        setErr(msg)
      } finally {
        setSaving(false)
      }
    },
    onError: () => {
      setErr('Google Login ล้มเหลว')
    }
  })

  useEffect(() => {
    if (emailParam && emailParam.includes('@')) {
      setForm(f => ({ ...f, email: emailParam }))
    }
  }, [emailParam])

  useEffect(() => {
    getCountries().then(r => setCountries(r?.data || [])).catch(() => setCountries([]))
  }, [])

  const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const passRx = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/
  const usernameRx = /^[A-Za-z0-9]+$/
  const countryRx = /^[A-Za-z]{2}$/

  const passScore = useMemo(() => {
    const p = form.pass || ''
    let s = 0
    if (p.length >= 8) s++
    if (/[0-9]/.test(p)) s++
    if (/[A-Z]/.test(p)) s++
    if (/[^A-Za-z0-9]/.test(p)) s++
    return s
  }, [form.pass])
  const passLabel = passScore >= 4 ? 'แข็งแรงมาก' : passScore === 3 ? 'แข็งแรง' : passScore === 2 ? 'ปานกลาง' : form.pass ? 'อ่อน' : ''

  const field = 'w-full pl-11 pr-4 border border-gray-300 rounded-xl py-3 text-base focus:ring-2 focus:ring-primary focus:outline-none placeholder-gray-400 bg-white/70 disabled:bg-gray-100'
  const pill = cls => `inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${cls}`
  const selectStyle = { control: b => ({ ...b, minHeight: 48, borderRadius: 12 }) }

  const submit = async e => {
    e.preventDefault()
    if (saving) return
    setErr(''); setOk('')

    // Detailed Validation
    if (!form.first_name.trim()) { setErr('กรุณากรอกชื่อจริง'); return }
    if (!form.last_name.trim()) { setErr('กรุณากรอกนามสกุล'); return }

    const eMail = form.email.trim()
    if (!eMail) { setErr('กรุณากรอกอีเมล'); return }
    if (!emailRx.test(eMail)) { setErr('รูปแบบอีเมลไม่ถูกต้อง'); return }

    if (!form.phone_code.trim()) { setErr('กรุณากรอกรหัสประเทศ'); return }
    if (!form.phone.trim()) { setErr('กรุณากรอกเบอร์โทรศัพท์'); return }

    const ct = (form.country || '').trim()
    if (!ct) { setErr('กรุณาเลือกประเทศ'); return }
    if (!countryRx.test(ct)) { setErr('รหัสประเทศไม่ถูกต้อง'); return }

    const u = form.username.trim()
    if (!u) { setErr('กรุณากรอกชื่อผู้ใช้'); return }
    if (!usernameRx.test(u)) { setErr('ชื่อผู้ใช้ไม่ถูกต้อง (ต้องเป็นภาษาอังกฤษและตัวเลขเท่านั้น)'); return }

    if (!form.pass) { setErr('กรุณากรอกรหัสผ่าน'); return }
    if (!passRx.test(form.pass)) { setErr('รหัสผ่านไม่ถูกต้อง (ต้องยาวอย่างน้อย 8 ตัว และมีทั้งตัวอักษรและตัวเลข)'); return }

    if (!form.pass2) { setErr('กรุณากรอกยืนยันรหัสผ่าน'); return }
    if (form.pass !== form.pass2) { setErr('รหัสผ่านยืนยันไม่ตรงกัน'); return }

    if (!form.acceptTerms) { setErr('กรุณายอมรับข้อกำหนดการใช้งาน'); return }
    if (!form.acceptPdpa) { setErr('กรุณายอมรับนโยบายความเป็นส่วนบุคคล'); return }

    setSaving(true)
    try {
      await register(
        form.username.trim(),
        form.email.trim(),
        form.pass,
        {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone_code: form.phone_code.trim().startsWith('+') ? form.phone_code.trim() : `+${form.phone_code.trim()}`,
          phone: form.phone.trim(),
          country: form.country.trim().toUpperCase(),
          accept_terms: true,
          accept_pdpa: true,
          accepted_at: new Date().toISOString(),
          terms_version: '2025-09-17',
          privacy_version: '2025-09-17'
        }
      )
      setOk('ส่งลิงก์ยืนยันไปยังอีเมลแล้ว โปรดตรวจสอบกล่องจดหมายเพื่อยืนยันการลงทะเบียน')
      nav('/login', { replace: true })
    } catch {
      setErr('ไม่สามารถส่งลิงก์ยืนยันได้ ชื่อผู้ใช้หรืออีเมลอาจถูกใช้งานแล้ว')
    } finally {
      setSaving(false)
    }
  }

  const countryValue = form.country ? { value: form.country, label: countries.find(c => c.iso2 === form.country)?.name_en || form.country } : null
  const countryOptions = countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})` }))

    const isComplete = !!(
      form.username.trim() &&
      form.email.trim() &&
      form.first_name.trim() &&
      form.last_name.trim() &&
      form.phone.trim() &&
      form.pass.trim() &&
      form.pass2.trim() &&
      form.acceptTerms &&
      form.acceptPdpa
    )

    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4">

      <motion.div
        initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        className="w-full max-w-xl bg-white/90 backdrop-blur-xl rounded-2xl px-8 py-8 shadow-2xl flex flex-col gap-6"
      >
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-extrabold text-primary tracking-wide">
            <img src="/images/app-logo.png" alt="Kuizy" className="h-20 mx-auto opacity-95" />
          </h1>
          <h2 className="text-2xl font-extrabold text-gray-900">ลงทะเบียน</h2>
          <p className="text-gray-500 text-sm">กรอกข้อมูลให้ครบ แล้วเราจะส่งลิงก์ยืนยันให้ทางอีเมล</p>
        </div>

        {err && (
          <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <div className="text-sm">{err}</div>
          </div>
        )}
        {!err && ok && (
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
            <CheckCircle2 className="h-5 w-5 mt-0.5" />
            <div className="text-sm">{ok}</div>
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                placeholder="ชื่อจริง"
                disabled={saving}
                value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                className={field}
              />
            </div>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                placeholder="นามสกุล"
                disabled={saving}
                value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                className={field}
              />
            </div>
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              placeholder="อีเมลสำหรับยืนยันบัญชี"
              autoComplete="email"
              disabled={saving}
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className={field}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div className="relative">
              <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Select
                classNamePrefix="rs"
                styles={selectStyle}
                options={countryOptions}
                value={countryValue}
                onChange={o => {
                  const c = countries.find(x => x.iso2 === (o?.value || ''))
                  setForm(f => ({ ...f, country: o?.value || 'TH', phone_code: c?.phone_code || f.phone_code }))
                }}
                placeholder="ประเทศ"
                isClearable
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                placeholder="+66"
                disabled={saving}
                value={form.phone_code}
                onChange={e => setForm(f => ({ ...f, phone_code: e.target.value }))}
                className={field}
              />
            </div>
            <div className="relative md:col-span-1">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                placeholder="เบอร์โทรศัพท์"
                disabled={saving}
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/[^0-9]/g, '') }))}
                className={field}
              />
            </div>
          </div>

          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              placeholder="ชื่อผู้ใช้ (อังกฤษและตัวเลขเท่านั้น)"
              autoComplete="username"
              pattern="[A-Za-z0-9]+"
              disabled={saving}
              value={form.username}
              onChange={e => {
                const v = e.target.value.replace(/[^A-Za-z0-9]/g, '')
                setForm(f => ({ ...f, username: v }))
              }}
              className={field}
            />
          </div>

          <div className="space-y-2">
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={show1 ? 'text' : 'password'}
                placeholder="รหัสผ่าน"
                autoComplete="new-password"
                disabled={saving}
                value={form.pass}
                onChange={e => setForm(f => ({ ...f, pass: e.target.value }))}
                className={field}
              />
              <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setShow1(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                {show1 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            <div className="flex items-center gap-2">
              {passLabel && <span className={pill(passScore >= 3 ? 'bg-emerald-100 text-emerald-700' : passScore === 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>ความแข็งแรง: {passLabel}</span>}
              <span className="text-xs text-gray-500">อย่างน้อย 8 ตัว มีตัวอักษรและตัวเลข</span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${form.pass.length >= 8 ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />8 ตัวอักษรขึ้นไป</div>
              <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${/[0-9]/.test(form.pass) ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />มีตัวเลขอย่างน้อย 1 ตัว</div>
              <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${/[A-Za-z]/.test(form.pass) ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />มีตัวอักษร</div>
              <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${/[^A-Za-z0-9]/.test(form.pass) ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />อักขระพิเศษ (แนะนำ)</div>
            </div>
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type={show2 ? 'text' : 'password'}
              placeholder="ยืนยันรหัสผ่าน"
              autoComplete="new-password"
              disabled={saving}
              value={form.pass2}
              onChange={e => setForm(f => ({ ...f, pass2: e.target.value }))}
              className={field}
            />
            <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setShow2(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
              {show2 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileText className="h-4 w-4 text-primary" />
              <span>โปรดอ่านและยอมรับเพื่อดำเนินการต่อ</span>
            </div>
            <TermsCheckbox checked={form.acceptTerms} onChange={v => setForm(f => ({ ...f, acceptTerms: v }))} />
            <PdpaCheckbox checked={form.acceptPdpa} onChange={v => setForm(f => ({ ...f, acceptPdpa: v }))} />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="h-4 w-4" />
              สามารถถอนความยินยอมด้านการตลาดหรือโปรไฟล์ได้ภายหลังในหน้าการตั้งค่า
            </div>
          </div>

          <button
            disabled={saving || !isComplete}
            className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 transition text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 shadow-lg"
          >
            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
            {saving ? 'กำลังส่งลิงก์…' : 'ส่งลิงก์ยืนยัน'}
          </button>
        </form>

        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <span className="flex-1 h-px bg-gray-300" />
          <span>หรือ</span>
          <span className="flex-1 h-px bg-gray-300" />
        </div>

        <div className="flex gap-2 flex-wrap justify-center">
          <button
            type="button"
            disabled={saving}
            onClick={() => googleLogin()}
            className="flex items-center justify-center gap-2 flex-1 min-w-[100px] border rounded-lg px-3 py-2 text-sm transition-colors border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-60"
          >
            Google
          </button>
        </div>

        {/* <SocialButtons /> */}

        <div className="text-center text-sm">
          มีบัญชีอยู่แล้ว? <Link to="/login" className="text-primary font-semibold">เข้าสู่ระบบ</Link>
        </div>
      </motion.div>
    </section>
  )
}
